import React, { useState, useEffect } from "react";
import { useOutletContext } from "react-router-dom";
import { format } from "date-fns";
import {
  Table,
  TableHeader,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { useToast } from "@/hooks/use-toast";
import { Trash2 } from "lucide-react";

// Enhanced template interface with additional fields
interface Template {
  id: string;
  title: string;
  type: string;
  dateCreated: string;
  dateLastEdited: string;
  dateLastViewed: string;
  summaryData: any;
  pricingData: any;
  questionsData: any;
  reverseAuctionData: any;
}

const Templates = () => {
  // Render the title in the outlet context
  if (useOutletContext() === "title-only") {
    return "Saved Templates";
  }
  
  const [templates, setTemplates] = useState<Template[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const { toast } = useToast();
  
  // Load templates from localStorage
  useEffect(() => {
    const savedTemplates = localStorage.getItem("saved-templates");
    if (savedTemplates) {
      try {
        const parsedTemplates = JSON.parse(savedTemplates);
        // Add mock data for the new fields if they don't exist
        const enhancedTemplates = parsedTemplates.map((template: any) => ({
          ...template,
          type: template.type || "General",
          dateLastEdited: template.dateLastEdited || template.dateCreated,
          dateLastViewed: template.dateLastViewed || template.dateCreated,
        }));
        setTemplates(enhancedTemplates);
      } catch (error) {
        console.error("Error parsing saved templates:", error);
      }
    }
  }, []);
  
  // Format date for display
  const formatDate = (dateString: string) => {
    try {
      return format(new Date(dateString), "MMM d, yyyy");
    } catch (error) {
      return "Invalid date";
    }
  };

  const handleDeleteTemplate = (templateId: string, templateName: string) => {
    const updatedTemplates = templates.filter(t => t.id !== templateId);
    setTemplates(updatedTemplates);
    localStorage.setItem("saved-templates", JSON.stringify(updatedTemplates));
    toast({
      title: "Template deleted",
      description: `"${templateName}" has been successfully deleted.`,
    });
  };
  
  // Calculate pagination
  const totalPages = Math.ceil(templates.length / itemsPerPage);
  const currentTemplates = templates.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );
  
  return (
    <div className="animate-fade-in">
      <div className="bg-white shadow rounded-lg p-6 border border-gray-200">
        <p className="text-gray-600 mb-4">
          View and manage your saved proposal templates.
        </p>
        
        {templates.length > 0 ? (
          <div className="space-y-4">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name of Matter</TableHead>
                    <TableHead>Type of Matter</TableHead>
                    <TableHead>Date Created</TableHead>
                    <TableHead>Date Last Edited</TableHead>
                    <TableHead>Date Last Viewed</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {currentTemplates.map((template) => (
                    <TableRow key={template.id}>
                      <TableCell className="font-medium">{template.title}</TableCell>
                      <TableCell>{template.type}</TableCell>
                      <TableCell>{formatDate(template.dateCreated)}</TableCell>
                      <TableCell>{formatDate(template.dateLastEdited)}</TableCell>
                      <TableCell>{formatDate(template.dateLastViewed)}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Button variant="outline" size="sm">
                            Use Template
                          </Button>
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button variant="outline" size="sm" className="text-destructive hover:text-destructive">
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent className="bg-white">
                              <AlertDialogHeader>
                                <AlertDialogTitle>Delete Template</AlertDialogTitle>
                                <AlertDialogDescription>
                                  Are you sure you want to delete "{template.title}"? This action cannot be undone.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Cancel</AlertDialogCancel>
                                <AlertDialogAction
                                  onClick={() => handleDeleteTemplate(template.id, template.title)}
                                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                >
                                  Delete
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            
            {totalPages > 1 && (
              <Pagination>
                <PaginationContent>
                  <PaginationItem>
                    <PaginationPrevious 
                      onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                      className={currentPage === 1 ? "pointer-events-none opacity-50" : ""}
                    />
                  </PaginationItem>
                  
                  {Array.from({length: totalPages}).map((_, index) => (
                    <PaginationItem key={index}>
                      <PaginationLink
                        isActive={currentPage === index + 1}
                        onClick={() => setCurrentPage(index + 1)}
                      >
                        {index + 1}
                      </PaginationLink>
                    </PaginationItem>
                  ))}
                  
                  <PaginationItem>
                    <PaginationNext 
                      onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                      className={currentPage === totalPages ? "pointer-events-none opacity-50" : ""}
                    />
                  </PaginationItem>
                </PaginationContent>
              </Pagination>
            )}
          </div>
        ) : (
          <div className="p-8 border border-dashed border-gray-300 rounded-lg text-center">
            <p className="text-gray-500">Your saved templates will appear here</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Templates;
